/**
 * Bring a person here from another partner - either moving them, or giving
 * them this partner as well.
 *
 * This exists because of a real support case: somebody at one branch needed to
 * see a sibling branch too. There are two right answers and they are not
 * interchangeable, so the dialog makes you pick rather than guessing:
 *
 * - **Also give access here** - they keep their branch and gain this one. Right
 *   when the second branch is genuinely extra.
 * - **Move them here** - they lose the old branch. Right when they have
 *   actually changed jobs.
 *
 * And when both branches sit under the same parent there is a third answer the
 * dialog surfaces rather than hides: moving them to the PARENT gives them both
 * branches, and every branch added under it later. Granting each branch one at
 * a time is the thing you regret in six months.
 */

import { useMemo, useState } from 'react'
import {
  CHOICE_SELECTED,
  ROW_SELECTED,
  ROW_UNSELECTED,
} from '@/admin/pages/partnerships/selection-styles'
import { PartnerRowLabel } from '@/admin/pages/partnerships/partner-row-label'
import { useTranslation } from 'react-i18next'
import { Info, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'

import {
  usePartnerPeople,
  useMoveAccess,
  type PartnerPerson,
} from '@/admin/hooks/use-partner-access-move'
import { usePartnerships } from '@/admin/hooks/use-partnerships'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'

interface MoveAccessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetPartnershipUuid: string
  targetPartnerName: string
  /** The target's parent, used to spot the "both under one group" case. */
  targetParentUuid: string | null
  targetParentName: string | null
  onChanged?: () => void
}

export function MoveAccessDialog({
  open,
  onOpenChange,
  targetPartnershipUuid,
  targetPartnerName,
  targetParentUuid,
  targetParentName,
  onChanged,
}: MoveAccessDialogProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [sourceUuid, setSourceUuid] = useState<string | undefined>(undefined)
  const [sourceName, setSourceName] = useState('')
  const [sourceParentUuid, setSourceParentUuid] = useState<string | null>(null)
  const [personUuid, setPersonUuid] = useState<string | undefined>(undefined)
  const [keepExisting, setKeepExisting] = useState(false)
  const debouncedSearch = useDebouncedValue(search, 250)

  const { data: partnerResults, isFetching: searchingPartners } = usePartnerships(
    open && debouncedSearch.length >= 2
      ? { search: debouncedSearch, page_size: '10' }
      : undefined,
  )

  const sourceCandidates = useMemo(
    () =>
      (partnerResults?.results ?? []).filter(
        (p) => p.uuid !== targetPartnershipUuid,
      ),
    [partnerResults, targetPartnershipUuid],
  )

  const {
    data: peopleData,
    isPending: peoplePending,
    isError: peopleFailed,
  } = usePartnerPeople(sourceUuid)
  const people: PartnerPerson[] = peopleData?.results ?? []

  const move = useMoveAccess(targetPartnershipUuid)
  // Reversing moves them back TO the source, so the hook is bound the other way.
  const reverseMove = useMoveAccess(sourceUuid)

  // The case worth naming out loud: both branches hang off one group, so the
  // group is the durable answer rather than granting branch by branch.
  const sharesParent =
    !!targetParentUuid && !!sourceParentUuid && targetParentUuid === sourceParentUuid

  const close = () => {
    setSearch('')
    setSourceUuid(undefined)
    setSourceName('')
    setSourceParentUuid(null)
    setPersonUuid(undefined)
    setKeepExisting(false)
    onOpenChange(false)
  }

  const confirm = async () => {
    if (!personUuid) {
      toast.error(
        t('partnerships.accessPickPerson', 'Choose the person to bring across.'),
      )
      return
    }
    try {
      const change = await move.mutateAsync({
        userUuid: personUuid,
        sourcePartnershipUuid: keepExisting ? null : (sourceUuid ?? null),
      })
      // Report what actually happened rather than assuming: withdrawing an
      // inherited user's access removes their membership, which is a bigger
      // thing than closing an access row and should be said.
      if (change.target_reactivated) {
        // Never silent: creating the membership un-suspended this partner, and
        // an operator who only meant to move one person needs to know.
        toast.warning(
          t(
            'partnerships.accessTargetReactivated',
            '{{target}} was suspended and is now active again.',
            { target: targetPartnerName },
          ),
        )
      }
      const reverseTo = change.source_partnership_uuid || null
      toast.success(
        change.membership_revoked
          ? t(
              'partnerships.accessMovedWithMembership',
              'Moved. They are no longer a member of {{source}}.',
              { source: sourceName },
            )
          : t('partnerships.accessChanged', 'Access updated.'),
        {
          // Only a MOVE is reversible in one step. "Give them both" added
          // access without taking any away, so there is nothing to put back
          // and offering Undo would imply the wrong thing.
          action:
            reverseTo && !keepExisting
              ? {
                  label: t('common.undo', 'Undo'),
                  onClick: () => {
                    void reverseMove
                      .mutateAsync({
                        userUuid: personUuid,
                        sourcePartnershipUuid: targetPartnershipUuid,
                      })
                      .then(() => {
                        onChanged?.()
                        toast.success(
                          t('partnerships.accessUndone', 'Put back.'),
                        )
                      })
                      .catch((e) =>
                        toastApiError(
                          e,
                          t(
                            'partnerships.accessUndoFailed',
                            'Could not put that back.',
                          ),
                        ),
                      )
                  },
                }
              : undefined,
        },
      )
      onChanged?.()
      close()
    } catch (err) {
      toastApiError(
        err,
        t('partnerships.accessChangeFailed', 'Could not update access.'),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-[560px]" data-testid="move-access-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('partnerships.accessTitle', 'Bring someone here')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'partnerships.accessIntro',
              'Find the partner they work with today, then choose whether they move or keep both.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="access-partner-search">
              {t('partnerships.accessSourceLabel', 'They currently work with')}
            </Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
              <Input
                id="access-partner-search"
                data-testid="access-partner-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(
                  'partnerships.accessSourcePlaceholder',
                  'Search partners by name',
                )}
                className="pl-8"
              />
            </div>

            <div className="max-h-[150px] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
              {debouncedSearch.length < 2 ? (
                <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  {t(
                    'partnerships.accessSourceHint',
                    'Type at least two characters to find a partner.',
                  )}
                </p>
              ) : searchingPartners && sourceCandidates.length === 0 ? (
                <p className="flex items-center gap-2 px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('partnerships.accessSearching', 'Searching')}
                </p>
              ) : sourceCandidates.length === 0 ? (
                <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
                  {t('partnerships.accessNoPartners', 'No partners match that.')}
                </p>
              ) : (
                sourceCandidates.map((p) => (
                  <Button
                    key={p.uuid}
                    variant="ghost"
                    data-testid={`access-source-${p.uuid}`}
                    onClick={() => {
                      setSourceUuid(p.uuid)
                      setSourceName(p.downstream_organisation?.name ?? '')
                      setSourceParentUuid(
                        p.downstream_organisation?.parent_organisation_uuid ?? null,
                      )
                      setPersonUuid(undefined)
                    }}
                    className={`h-auto w-full justify-start rounded-none px-3 py-2 text-[13px] font-normal ${
                      sourceUuid === p.uuid ? ROW_SELECTED : ROW_UNSELECTED
                    }`}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <PartnerRowLabel
                        name={p.downstream_organisation?.name}
                        attributeSummary={p.attribute_summary}
                      />
                      {!p.is_active && (
                        <Badge variant="outline" className="shrink-0">
                          {t('partnerships.regroupSuspendedTag', 'Suspended')}
                        </Badge>
                      )}
                    </span>
                  </Button>
                ))
              )}
            </div>
          </div>

          {sourceUuid && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label>{t('partnerships.accessPersonLabel', 'Person')}</Label>
                <div
                  className="max-h-[150px] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]"
                  data-testid="access-people"
                >
                  {peoplePending ? (
                    <p className="flex items-center gap-2 px-3 py-3 text-[12px] text-[var(--ink-3)]">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('partnerships.accessLoadingPeople', 'Loading people')}
                    </p>
                  ) : peopleFailed ? (
                    <p className="px-3 py-3 text-[12px] text-[var(--status-error)]">
                      {t(
                        'partnerships.accessPeopleFailed',
                        'Could not load the people for that partner.',
                      )}
                    </p>
                  ) : people.length === 0 ? (
                    <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
                      {t(
                        'partnerships.accessNoPeople',
                        'Nobody has access to that partner yet.',
                      )}
                    </p>
                  ) : (
                    people.map((person) => (
                      <Button
                        key={person.user_uuid}
                        variant="ghost"
                        data-testid={`access-person-${person.user_uuid}`}
                        onClick={() => setPersonUuid(person.user_uuid)}
                        className={`h-auto w-full justify-start rounded-none px-3 py-2 text-[13px] font-normal ${
                          personUuid === person.user_uuid
                            ? ROW_SELECTED
                            : ROW_UNSELECTED
                        }`}
                      >
                        <span className="flex w-full flex-col items-start">
                          <span>{person.user_name || person.user_email}</span>
                          <span className="text-[11px] text-[var(--ink-3)]">
                            {person.user_email}
                          </span>
                        </span>
                      </Button>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  variant="ghost"
                  data-testid="access-mode-move"
                  onClick={() => setKeepExisting(false)}
                  className={`h-auto w-full justify-start rounded-[6px] border-[0.5px] border-[var(--rule)] px-3 py-2 text-left text-[13px] font-normal ${
                    !keepExisting ? CHOICE_SELECTED : ''
                  }`}
                >
                  {t('partnerships.accessModeMove', 'Move them to {{target}}', {
                    target: targetPartnerName,
                  })}
                </Button>
                <Button
                  variant="ghost"
                  data-testid="access-mode-both"
                  onClick={() => setKeepExisting(true)}
                  className={`h-auto w-full justify-start rounded-[6px] border-[0.5px] border-[var(--rule)] px-3 py-2 text-left text-[13px] font-normal ${
                    keepExisting ? CHOICE_SELECTED : ''
                  }`}
                >
                  {t('partnerships.accessModeBoth', 'Give them both')}
                </Button>
              </div>

              {sharesParent && targetParentName && (
                <div
                  className="flex gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-3 text-[12px] text-[var(--ink-2)]"
                  data-testid="access-group-hint"
                >
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  <span>
                    {t(
                      'partnerships.accessGroupHint',
                      'Both sit under {{parent}}. Moving them to {{parent}} instead covers both, and any location added under it later.',
                      { parent: targetParentName },
                    )}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={move.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {/* Enabled with nothing picked - the click says what is missing. */}
          <Button
            onClick={confirm}
            data-testid="access-confirm"
            disabled={move.isPending}
          >
            {move.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            {keepExisting
              ? t('partnerships.accessConfirmBoth', 'Give both')
              : t('partnerships.accessConfirmMove', 'Move here')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
